/* eslint-disable no-bitwise */
export const JWT_SECRET = 'devias-top-secret-key';
export const JWT_EXPIRES_IN = 3600 * 24 * 2; // 2 days

// Since we are unable to sign a JWT in a browser
// because "jsonwebtoken" library is available on server side only, NodeJS environment
// we simply simulate a signed token, no complex checks because on server side
// you're using the library
export const sign = (payload, privateKey, header) => {
  const now = new Date();
  const newHeader = { ...header };
  newHeader.expiresIn = new Date(now.getTime() + newHeader.expiresIn);
  const encodedHeader = btoa(JSON.stringify(newHeader));
  const encodedPayload = btoa(JSON.stringify(payload));
  const signature = btoa(Array
    .from(encodedPayload)
    .map((item, key) => (String.fromCharCode(item.charCodeAt(0) ^ privateKey[key
      % privateKey.length].charCodeAt(0))))
    .join(''));

  return `${encodedHeader}.${encodedPayload}.${signature}`;
};

// Since we create a fake signed token, we have to implement a fake jwt decode
// platform to simulate "jwt-decode" library.
export const decode = (token) => {
  const [encodedHeader, encodedPayload, signature] = token.split('.');
  const header = JSON.parse(atob(encodedHeader));
  const payload = JSON.parse(atob(encodedPayload));
  const now = new Date();

  if (now < header.expiresIn) {
    throw new Error('Expired token');
  }

  const verifiedSignature = btoa(Array
    .from(encodedPayload)
    .map((item, key) => (String.fromCharCode(item.charCodeAt(0) ^ JWT_SECRET[key
      % JWT_SECRET.length].charCodeAt(0))))
    .join(''));

  if (verifiedSignature !== signature) {
    throw new Error('Invalid signature');
  }

  return payload;
};

export const verify = (token, privateKey) => {
  const [encodedHeader, encodedPayload, signature] = token.split('.');
  const header = JSON.parse(atob(encodedHeader));
  const payload = JSON.parse(atob(encodedPayload));
  const now = new Date();

  if (now < header.expiresIn) {
    throw new Error('Expired token');
  }

  const verifiedSignature = btoa(Array
    .from(encodedPayload)
    .map((item, key) => (String.fromCharCode(item.charCodeAt(0) ^ privateKey[key
      % privateKey.length].charCodeAt(0))))
    .join(''));

  if (verifiedSignature !== signature) {
    throw new Error('Invalid signature');
  }

  return payload;
};

// --- Real backend JWT helpers (the access token issued by api.accounts) ---

// Fallback session lifetime used only if a token can't be decoded.
// Keep in sync with the backend JWT TTL (signOptions.expiresIn).
export const FALLBACK_TOKEN_LIFETIME_MS = 9 * 60 * 60 * 1000; // 9h

// Read the `exp` claim (seconds) from a real backend JWT and return it as
// milliseconds-since-epoch. Returns null for malformed/missing tokens so
// callers can fall back to FALLBACK_TOKEN_LIFETIME_MS.
export const getJwtExpiryMs = (token) => {
  try {
    const payload = token.split('.')[1];
    if (!payload) return null;
    // Base64url -> base64
    const normalized = payload.replace(/-/g, '+').replace(/_/g, '/');
    const decoded = JSON.parse(atob(normalized));
    if (decoded && typeof decoded.exp === 'number') {
      return decoded.exp * 1000;
    }
  } catch {
    /* malformed token — fall back */
  }
  return null;
};

// Persist a freshly issued/refreshed access token plus a derived expiry,
// so refresh timing tracks the server's real token lifetime.
export const persistAccessToken = (token) => {
  const expiry = getJwtExpiryMs(token) ?? Date.now() + FALLBACK_TOKEN_LIFETIME_MS;
  localStorage.setItem('accessToken', token);
  localStorage.setItem('tokenExpiry', expiry.toString());
  return expiry;
};
